import { useCallback, useState } from "react";
import swal from "sweetalert";
import { requestOtp, subscribe as subscribeApi } from "../services/homeService";
import { setCookie } from "../utils/CommonFunction";
import tagManagerEvents from "../utils/GoogleTagManager";
import { EMPTY_FORM_ERROR, type FormError } from "../types/home";

const INVALID_OTP_MESSAGE =
  "OTP is Invalid or Expired, Please subscribe again";

export interface HomeSubscription {
  otpStatus: boolean;
  loader: boolean;
  error: FormError;
  /** The phone number that requested the current OTP (used for resend). */
  phNumber: string;
  setLoader: (value: boolean) => void;
  setUserPackage: (value: string) => void;
  setIsActive: (value: boolean) => void;
  /** Request an OTP for the given phone number. */
  subscribe: (phone: string) => Promise<void>;
  /** Confirm the OTP and finalise the subscription. */
  verifyOtp: (otp: string) => Promise<void>;
}

/**
 * Owns the entire "subscribe & win" telco flow: OTP request, OTP verification,
 * cookie persistence and the related GTM events / error state.
 *
 * `token` is taken implicitly from `global.token` by the API layer for GETs and
 * passed explicitly for the subscribe POST.
 */
export const useHomeSubscription = (
  token: string | null,
  utmSource: string
): HomeSubscription => {
  const [otpStatus, setOtpStatus] = useState(false);
  const [loader, setLoader] = useState(false);
  const [error, setError] = useState<FormError>(EMPTY_FORM_ERROR);
  const [phNumber, setPhNumber] = useState<string>("");
  const [userPackage, setUserPackage] = useState("");
  const [isActive, setIsActive] = useState(false);

  const subscribe = useCallback(
    async (phone: string): Promise<void> => {
      try {
        const response = await requestOtp(phone);

        if (response.status === 200) {
          tagManagerEvents("Request_OTP", utmSource);
          setOtpStatus(true);
          setPhNumber(phone);
          setError(EMPTY_FORM_ERROR);
        } else {
          swal("Something is not right, Please try again later");
        }

        setLoader(false);
      } catch (error) {
        console.warn("subscribe error ::", error);
      }
    },
    [utmSource]
  );

  const verifyOtp = useCallback(
    async (otp: string): Promise<void> => {
      try {
        const response = await subscribeApi({
          msisdn: phNumber,
          userPackage,
          isActive,
          otp,
          token,
        });

        if (response.status === 208) {
          tagManagerEvents("subscribe", utmSource);
          setCookie(
            phNumber,
            response.response.token,
            response.response.subscribeId
          );
          window.location.href = "/";
        } else if (response.status === 400) {
          setError({ Error: INVALID_OTP_MESSAGE, isTrue: true });
          tagManagerEvents("OTP_FAILED", utmSource);
          setOtpStatus(false);
        }
      } catch (error) {
        tagManagerEvents("Subscribe_Unsuccessfully", utmSource);
        console.log("susbcribe home error", error);
      }
    },
    [phNumber, userPackage, isActive, token, utmSource]
  );

  return {
    otpStatus,
    loader,
    error,
    phNumber,
    setLoader,
    setUserPackage,
    setIsActive,
    subscribe,
    verifyOtp,
  };
};
